import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	useHistory,
	useLocation
} from 'react-router';
import {
	Divider,
	Form,
	Icon,
	Image,
	Label,
	Message,
	Tab
} from 'semantic-ui-react';

import { stateOptions } from '../../../../../services/constants';
import {
	log,
	print
} from '../../../../../services/utils';

import { updateContactPhoto } from '../../../../../api/nora/contacts';
import { uploadAsset } from '../../../../../api/nora/assets';

import { useAuthState } from '../../../../../contexts/AuthContext';
import {
	useContactState,
	useContactAction
} from '../../../../../contexts/ContactsContext';
import { useUIAction } from '../../../../../contexts/UIContext';

import Actions from '../../../../Shared/Actions';
import Alert from '../../../../Alert';
import DDFileUploadModal from '../../../../DDFileUpload';
import FormCheckbox from '../../../../Form/FormCheckbox';
import FormInput from '../../../../Form/FormInput';
import FormSelect from '../../../../Form/FormSelect';
import FormTextArea from '../../../../Form/FormTextArea';
import TagInput from '../../../../Shared/TagInput';
import FormErrors from '../../../../Shared/FormErrors';

const InfoTab = ( {
	errors,
	forceValidation,
	formData,
	formDataHasChanged,
	handleSave,
	locks,
	resetFormData,
	updateField,
	validate
} ) => {
	const history = useHistory();
	const { state: navigationState } = useLocation();

	const { user: { permissions } } = useAuthState();
	const {
		contact,
		contactTypes,
		contactTags,
		managementOrganizations,
		agencyOrganizations,
		vendorOrganizations
	} = useContactState();
	const {
		setTags,
		setVendors
	} = useContactAction();
	const { setApiErrorAlert } = useUIAction();

	const [ editable, setEditable ] = useState( !formData.id );
	const [ fileUploadModal, setFileUploadModal ] = useState( false );
	const [ organizationOptions, setOrganizationOptions ] = useState( [] );
	const [ removePhotoConfirmAlert, setRemovePhotoConfirmAlert ] = useState( false );
	const [ isSaving, setIsSaving ] = useState( false );

	useEffect( () => {
		setEditable( !formData.id );
	}, [ formData.id ] );

	useEffect( () => {
		if ( formData.type ) {
			switch ( formData.type ) {
				case 2: // staff
					setOrganizationOptions( managementOrganizations );
					break;
				case 3: // agency
					setOrganizationOptions( agencyOrganizations );
					break;
				case 4: // vendor
				case 12: // participant
					setOrganizationOptions( vendorOrganizations );
					break;
				default:
					log( 'invalid organization type' );
					setOrganizationOptions( [] );
					break;
			}
		}
	}, [
		agencyOrganizations,
		formData.type,
		managementOrganizations,
		vendorOrganizations
	] );

	useEffect( () => () => {
		setVendors();
	}, [ setVendors ] );

	const mapFormErrors = useCallback(
		() => Object.values( errors ).map( errors => errors.map( error => error.error
			? <Message.Item key={ error.fieldLabel }>{ error.fieldLabel }</Message.Item>
			: null ) ),
		[ errors ]
	);

	const save = useCallback( async () => {
		const cb = () => {
			if ( !validate() ) {
				setVendors();
				setEditable( false );
			}
		};

		setIsSaving( true );
		await handleSave( cb );
		setIsSaving( false );
	}, [
		handleSave,
		setVendors,
		validate
	] );

	const saveAction = useMemo(
		() => ( {
			action: save,
			disable: !formDataHasChanged || isSaving,
			icon: 'save',
			permission: true,
			text: formData.id === 0
				? 'Save As New Contact'
				: 'Save'
		} ),
		[
			formData.id,
			formDataHasChanged,
			isSaving,
			save
		]
	);

	const cancel = useCallback( () => {
		resetFormData();

		if ( formData.id === 0 ) {
			if ( navigationState?.to ) {
				history.push( navigationState.to );
			} else {
				history.push( '/operations/contacts' );
			}
		} else {
			setEditable( false );
		}
	}, [
		formData.id,
		history,
		navigationState,
		resetFormData
	] );

	const cancelAction = useMemo(
		() => ( {
			action: cancel,
			icon: 'times',
			permission: true,
			text: 'Cancel'
		} ),
		[ cancel ]
	);

	const edit = useCallback( () => {
		setEditable( true );
	}, [] );

	const editAction = useMemo( () => ( {
		action: edit,
		disable: !permissions.editContacts.isAvailable || formData.statusID === 5,
		icon: 'pencil',
		permission: permissions.editContacts.isAvailable,
		text: 'Edit'
	} ), [
		edit,
		formData.statusID,
		permissions
	] );

	const printAction = useMemo( () => ( {
		action: print,
		disable: validate(),
		icon: 'print',
		permission: permissions.viewContacts.canExecute,
		text: 'Print'
	} ), [
		permissions,
		validate
	] );

	const duplicate = useCallback( () => {
		let {
			id,
			firstName,
			lastName,
			// eslint-disable-next-line no-unused-vars
			email,
			...initialData
		} = formData;

		history.push( {
			pathname: '/operations/contacts/0',
			state: {
				text: `\u2190\u00A0\u00A0Back to ${ firstName } ${ lastName }`,
				to: {
					pathname: `/operations/contacts/${ id }`,
					state: { activeIndex: 1 }
				},
				initialData
			}
		} );
	}, [
		formData,
		history
	] );

	const duplicateAction = useMemo( () => ( {
		action: duplicate,
		disable: validate(),
		helpId: 7,
		icon: 'add',
		permission: permissions.addContacts.canExecute,
		text: 'Duplicate Contact'
	} ), [
		duplicate,
		permissions.addContacts.canExecute,
		validate
	] );

	const actions = useMemo(
		() => editable
			? [
				cancelAction,
				saveAction
			]
			: [
				duplicateAction,
				printAction,
				editAction
			],
		[
			cancelAction,
			duplicateAction,
			editable,
			saveAction,
			printAction,
			editAction
		]
	);

	const handleUploadPhoto = useCallback( () => {
		setFileUploadModal( true );
	}, [] );

	const onUpload = useCallback( async ( { uid } ) => {
		if ( formData.id ) {
			try {
				const response = await updateContactPhoto( formData.id, uid );

				updateField( {
					target: {
						name: 'photo',
						value: response.uri
					}
				}, true );
			} catch ( err ) {
				setApiErrorAlert( err );
			}
		}
	}, [
		formData.id,
		setApiErrorAlert,
		updateField
	] );

	const handleRemovePhoto = useCallback( () => {
		setRemovePhotoConfirmAlert( true );
	}, [] );

	const removePhoto = useCallback( () => {
		if ( formData.id ) {
			try {
				updateContactPhoto( formData.id, 0 );

				updateField( {
					target: {
						name: 'photo',
						value: null
					}
				}, true );
			} catch ( err ) {
				setApiErrorAlert( err );
			}
		}
	}, [
		formData.id,
		setApiErrorAlert,
		updateField
	] );

	const closeFileUploadModal = useCallback( () => {
		setFileUploadModal( false );
	}, [] );

	const closeRemovePhotoConfirmAlert = useCallback( () => {
		setRemovePhotoConfirmAlert( false );
	}, [] );

	// organizations
	const handleContactTypeChange = useCallback( e => {
		updateField( e ); // change type
		updateField( {
			target: {
				name: 'organization',
				value: e.target.value === 2
					? 1
					: ''
			}
		} ); // set organization
		updateField( {
			target: {
				name: 'organizationName',
				value: ''
			}
		} ); // clear out the organization name
	}, [ updateField ] );

	// vendors
	const handleAddVendor = useCallback( ( e, { value } ) => {
		setOrganizationOptions( prevState => {
			const updatedOptions = prevState.filter( opt => opt.value !== '0' );

			return [
				{
					text: value,
					value: '0'
				},
				...updatedOptions
			];
		} );
		updateField( {
			target: {
				name: 'organization',
				value: '0'
			}
		} );
		updateField( {
			target: {
				name: 'organizationName',
				value
			}
		} );
	}, [ updateField ] );

	// tags
	const addNewTag = useCallback( async tag => {
		await setTags();
		updateField( {
			target: {
				name: 'tags',
				value: [
					...formData.tags,
					tag.id
				]
			}
		} );
	}, [
		formData.tags,
		setTags,
		updateField
	] );

	const emailIcon = useMemo(
		() => !errors.email.some( error => error.error )
			? <Icon
				color="black"
				link
				name="mail"
				onClick={ () => console.log( 'email contact' ) }
			/>
			: null
		, [ errors.email ]
	);

	const countryCode = useMemo( () => <Label basic><span>+1&nbsp;</span></Label>, [] );

	return (
		<Tab.Pane as="div">
			{ log( 'infoTab' ) }
			<Actions actions={ actions } />
			<div className="contact-form-container">
				<div className="no-print">
					<Image
						size="small"
						src={ formData.photo
							? formData.photo
							: 'https://react.semantic-ui.com/images/wireframe/image.png'
						}
					/>
					{ editable
						? formData.photo
							? <small className="image-upload-actions">
								<span onClick={ handleUploadPhoto }>edit</span>&nbsp;&nbsp;|&nbsp;&nbsp;<span onClick={ handleRemovePhoto }>remove</span>
							</small>
							: <small className="image-upload-actions">
								<span onClick={ handleUploadPhoto }>upload</span>
							</small>
						: null
					}
				</div>

				<Form
					autoComplete="off"
					error={ forceValidation && validate() }
				>
					{ formData.type === 3 && formData.statusID === 5
						? <Message
							compact
							icon="exclamation circle"
							header={ `Inactive Staff Member for ${ contact.organization.name }` }
						>
						</Message>
						: null
					}
					<input
						className="hidden-input"
						name="name"
					/>
					<Form.Group>
						<FormSelect
							disabled={ locks.includes( 'type' ) }
							errors={ errors.type }
							fieldLabel="Contact Type"
							forceValidation={ forceValidation }
							name="type"
							onChange={ handleContactTypeChange }
							options={ contactTypes }
							readOnly={ !editable }
							required
							value={ formData.type || '' }
							width={ 4 }
						/>
					</Form.Group>

					<Form.Group>
						<TagInput
							addNewTag={ addNewTag }
							availableTags={ contactTags }
							disabled={ locks.includes( 'tags' ) }
							handleUpdate={ updateField }
							tags={ formData.tags || [] }
							readOnly={ !editable }
						/>
					</Form.Group>

					<Divider
						hidden
						style={ { margin: '1.5rem 0' } }
					/>

					<Form.Group>
						<FormInput
							disabled={ locks.includes( 'firstName' ) }
							errors={ errors.firstName }
							fieldLabel="First Name"
							forceValidation={ forceValidation }
							maxLength={ 255 }
							name="firstName"
							onChange={ updateField }
							readOnly={ !editable }
							required
							value={ formData.firstName || '' }
							width={ 6 }
						/>
						<FormInput
							disabled={ locks.includes( 'lastName' ) }
							errors={ errors.lastName }
							fieldLabel="Last Name"
							forceValidation={ forceValidation }
							maxLength={ 255 }
							name="lastName"
							onChange={ updateField }
							readOnly={ !editable }
							required
							value={ formData.lastName || '' }
							width={ 6 }
						/>
					</Form.Group>

					<Form.Group>
						<FormSelect
							allowAdditions={ formData.type === 4 || formData.type === 12 } // vendor or participant
							disabled={ locks.includes( 'organization' ) }
							errors={ errors.organization }
							fieldLabel="Organization"
							forceValidation={ forceValidation }
							name="organization"
							onAddItem={ formData.type === 4 || formData.type === 12
								? handleAddVendor
								: null
							} // vendor or participant
							onChange={ updateField }
							options={ organizationOptions }
							readOnly={ !editable }
							required
							search
							value={ formData.organization || '' }
							width={ 6 }
						/>
					</Form.Group>

					<Form.Group>
						<FormInput
							fieldLabel="Title"
							name="title"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.title || '' }
							width={ 6 }
						/>
					</Form.Group>

					<Divider
						hidden
						style={ { margin: '1.5rem 0' } }
					/>

					<Form.Group>
						<FormInput
							disabled={ locks.includes( 'address' ) }
							fieldLabel="Address"
							name="address"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.address || '' }
							width={ 10 }
						/>
					</Form.Group>

					<Form.Group>
						<FormInput
							disabled={ locks.includes( 'address2' ) }
							fieldLabel="Address Line 2"
							name="address2"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.address2 || '' }
							width={ 10 }
						/>
					</Form.Group>

					<Form.Group>
						<FormInput
							disabled={ locks.includes( 'city' ) }
							fieldLabel="City"
							name="city"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.city || '' }
							width={ 6 }
						/>
						<FormSelect
							disabled={ locks.includes( 'state' ) }
							fieldLabel="State"
							maxLength={ 2 }
							name="state"
							onChange={ updateField }
							options={ stateOptions }
							readOnly={ !editable }
							search
							value={ formData.state || '' }
							width={ 2 }
						/>
						<FormInput
							disabled={ locks.includes( 'zip' ) }
							errors={ errors.zip }
							fieldLabel="Zip"
							forceValidation={ forceValidation }
							keyDown="integer"
							maxLength={ 5 }
							name="zip"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.zip || '' }
							width={ 2 }
						/>
					</Form.Group>

					<Divider
						hidden
						style={ { margin: '1.5rem 0' } }
					/>

					<Form.Group>
						<FormInput
							disabled={ locks.includes( 'email' ) }
							errors={ errors.email }
							fieldLabel="Email"
							forceValidation={ forceValidation }
							icon={ emailIcon }
							maxLength={ 254 }
							name="email"
							readOnly={ !editable }
							required
							value={ formData.email || '' }
							onChange={ updateField }
							width={ 8 }
						/>
					</Form.Group>

					<Form.Group>
						<FormInput
							disabled={ locks.includes( 'phone' ) }
							errors={ errors.phone }
							fieldLabel="Phone"
							forceValidation={ forceValidation }
							keyDown="integer"
							inputLabel={ countryCode }
							mask="phone"
							maxLength={ 14 }
							name="phone"
							onChange={ updateField }
							readOnly={ !editable }
							required
							value={ formData.phone || '' }
							width={ 6 }
						/>
						<FormInput
							disabled={ locks.includes( 'ext' ) }
							fieldLabel="Ext."
							forceValidation={ forceValidation }
							keyDown="integer"
							name="ext"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.ext || '' }
							width={ 2 }
						/>
						<FormCheckbox
							checked={ formData.SMSEnabled }
							disabled={ locks.includes( 'SMSEnabled' ) }
							fieldLabel="SMS Enabled"
							name="SMSEnabled"
							onChange={ updateField }
							readOnly={ !editable }
							width={ 3 }
						/>
					</Form.Group>
					<Form.Group>
						<FormInput
							disabled={ locks.includes( 'fax' ) }
							errors={ errors.fax }
							fieldLabel="Fax"
							forceValidation={ forceValidation }
							keyDown="integer"
							inputLabel={ countryCode }
							mask="phone"
							maxLength={ 14 }
							name="fax"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.fax || '' }
							width={ 6 }
						/>
					</Form.Group>

					<Divider
						hidden
						style={ { margin: '1.5rem 0' } }
					/>

					<Form.Group>
						<FormTextArea
							disabled={ locks.includes( 'comments' ) }
							fieldLabel="Comments"
							name="comments"
							onChange={ updateField }
							readOnly={ !editable }
							rows={ 6 }
							value={ formData.comments || '' }
							width={ 16 }
						/>
					</Form.Group>
					<FormErrors mapFormErrors={ mapFormErrors } />
				</Form>

			</div>
			<DDFileUploadModal
				closeModal={ closeFileUploadModal }
				onUpload={ onUpload }
				open={ fileUploadModal }
				upload={ uploadAsset}
			/>
			<Alert
				action={ removePhoto }
				actionText="Remove Photo"
				name="contact-remove-photo-confirm-alert"
				message="Are you sure?"
				open={ removePhotoConfirmAlert }
				closeModal={ closeRemovePhotoConfirmAlert }
				closeText="Cancel"
				type="confirm"
			/>
		</Tab.Pane>
	);
};

InfoTab.propTypes = {
	errors: PropTypes.object,
	forceValidation: PropTypes.bool,
	formData: PropTypes.object,
	formDataHasChanged: PropTypes.bool,
	handleSave: PropTypes.func,
	locks: PropTypes.array,
	resetFormData: PropTypes.func,
	updateField: PropTypes.func,
	validate: PropTypes.func
};

export default memo( InfoTab );
